import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getRiskColor, formatKeyForDisplay, formatBooleanValue } from '../../utils/riskUtils';

const translationMap = {
  'coarse_facial_features': 'Грубые черты лица',
  'full_eyebrows': 'Густые брови',
  'short_wide_neck': 'Короткая широкая шея',
  'macrocephaly': 'Макроцефалия',
  'prominent_forehead': 'Выступающий лоб',
  'wide_nose_with_flared_nostrils': 'Широкий нос с расширенными ноздрями',
  'large_tongue': 'Большой язык',
  'thickened_skin': 'Утолщенная кожа',
  'short_stature': 'Низкий рост',
  'curved_fingers': 'Искривленные пальцы',
  'broad_chest': 'Широкая грудная клетка',
  'umbilical_or_inguinal_hernia': 'Пупочная или паховая грыжа',
  'грубые_черты_лица': 'Грубые черты лица',
  'густые_брови': 'Густые брови',
  'короткая_широкая_шея': 'Короткая широкая шея',
  'макроцефалия': 'Макроцефалия',
  'выступающий_лоб': 'Выступающий лоб',
  'широкий_нос_с_расширенными_ноздрями': 'Широкий нос с расширенными ноздрями',
  'большой_язык': 'Большой язык',
  'утолщенная_кожа': 'Утолщенная кожа',
  'низкий_рост': 'Низкий рост',
  'искривленные_пальцы': 'Искривленные пальцы',
  'широкая_грудная_клетка': 'Широкая грудная клетка',
  'пупочная_или_паховая_грыжа': 'Пупочная или паховая грыжа'
};

const HanterResults = ({ data }) => {
  // Choose data source (Russian or English variant)
  const hanterData = data.hanter_check_ru || data.hanter_check;
  
  // Если нет данных о синдроме Хантера, проверяем наличие риска в нейрорезультатах
  const hanterRisk = !hanterData && (data.neuro_result_ru?.риск_хантера || data.neuro_result?.hanter_risk);
  const hanterScore = !hanterData && data.validation?.seven_years_old_boy_face ? 1 : 0;
  
  // Если нет ни данных о синдроме Хантера, ни риска, возвращаем null
  if (!hanterData && !hanterRisk) {
    console.log('❌ HanterResults: нет данных о синдроме Хантера');
    return null;
  }
  
  console.log('📊 HanterResults:', {
    'hanterData': !!hanterData,
    'hanterRisk': hanterRisk,
    'hanterScore': hanterScore,
    'seven_years_old_boy_face': data.validation?.seven_years_old_boy_face
  });
  
  // Extract risk and score data
  const riskValue = hanterData ? (hanterData.риск || hanterData.risk || 'неизвестно') : hanterRisk;
  const scoreValue = hanterData ? (hanterData.баллы || hanterData.score || 0) : hanterScore;
  const riskColor = getRiskColor(riskValue);
  
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h3" gutterBottom sx={{ 
          fontSize: '1.125rem', 
          color: 'text.primary', 
          fontWeight: 500 
        }}>
          Результаты анализа на синдром Хантера
        </Typography>
        
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip 
            label={`Риск: ${riskValue}`} 
            sx={{ 
              backgroundColor: riskColor,
              color: 'white',
              fontWeight: 500,
              fontSize: '0.875rem',
              height: 28,
            }}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            <strong>Баллы:</strong> {scoreValue}
          </Typography>
        </Box>
      </Box>
      
      {/* Отображаем детали признаков только если есть полные данные */}
      {hanterData && (
        <TableContainer component={Paper} elevation={0} 
          sx={{ 
            mb: 3, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Признак</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Наличие</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(hanterData).map(([key, value]) => {
                if (key === 'риск' || key === 'risk' || key === 'баллы' || key === 'score') {
                  return null;
                }
                
                // Get translated name or format the key
                const displayName = translationMap[key] || formatKeyForDisplay(key);
                
                return (
                  <TableRow key={key} hover>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{displayName}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                        {value === true ? (
                          <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem', color: '#d32f2f' }} />
                        ) : value === false ? (
                          <CancelIcon sx={{ mr: 1, fontSize: '1rem', color: '#4caf50' }} />
                        ) : null}
                        {formatBooleanValue(value)}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Если у нас только риск, но нет детальных данных */}
      {!hanterData && hanterRisk && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {data.validation?.seven_years_old_boy_face
              ? 'Обнаружены признаки, указывающие на возможное наличие синдрома Хантера. Рекомендуется обратиться к специалисту для дополнительного обследования.'
              : 'Риск синдрома Хантера определен по результатам автоматического анализа фотографии. Для получения более подробной информации требуется фотография лица ребенка.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HanterResults; 